import './utils/buttons.css';
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Auth } from "aws-amplify";
import { useSignal } from "@preact/signals-react";
import TextEdit from "../../molecules/TextEdit";
import SelectTextField from "../../molecules/SelectTextField";
import PageForm from "../../templates/PageForm";
import appConfig from '../../../config/app-config';
import { CREDIT_OPERATION_TYPE_OPTIONS } from './constants/creditOperationTypes';

const { address: API_URL, key: API_KEY } = appConfig.apis.contractsManagementApiEnv;
const { address: PERSON_SERVICE_URL, key: API_KEY_PERSON } = appConfig.apis.personApiEnv;

const BACKOFFICE_URL = appConfig.apis.backofficeApiUrl;
const CONTRACTS_LIST_URL = '/contracts/0/100/id/ASC/s=;';
const MAX_SEARCH_RESULTS = 10;
const TABLE_LOADING_HEIGHT = '320px';
const SEARCH_DEBOUNCE_MS = 500;
const MIN_SEARCH_LENGTH = 3;

interface CreditOperationFormData {
    // CreditOperation fields
    operation_number: string;
    originator: string;
    external_link: string;
    credit_product_type_name: string;

    // ReceivableAssignment fields
    with_recourse: boolean;
    assignor: string;
}

const OperationReceivablesAssignmentFormPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const operationType = searchParams.get('operation_type');
    const urlApiName = searchParams.get('apiName');
    const urlApiVersion = searchParams.get('apiVersion');
    const [isLoading, setIsLoading] = useState(false);
    const [isSearchingCedente, setIsSearchingCedente] = useState(false);
    const [cedentes, setCedentes] = useState<Array<any>>([]);
    const [searchResults, setSearchResults] = useState<Array<any>>([]);
    const [searchCedente, setSearchCedente] = useState('');
    const [selectedCedente, setSelectedCedente] = useState('');
    const [selectedCedenteName, setSelectedCedenteName] = useState('');
    const [CONTRACT_TEMPLATE_ID, setCONTRACT_TEMPLATE_ID] = useState<number | null>(null);
    const apiVersion = useSignal('');

    const debounceTimer = useRef<NodeJS.Timeout | null>(null);
    const abortController = useRef<AbortController | null>(null);

    const getRowClassName = (index: number) => {
        const baseClasses = index % 2 === 0 ? 'bg-slate-100 dark:bg-slate-700' : 'bg-white dark:bg-gray-800';
        return `${baseClasses} hover:bg-slate-300 dark:hover:bg-slate-600 hover:shadow-md border-b border-gray-200 dark:border-gray-600`;
    };



    const [formData, setFormData] = useState<CreditOperationFormData>({
        // CreditOperation fields
        operation_number: '',
        originator: '',
        external_link: '',
        credit_product_type_name: '',

        // ReceivableAssignment fields
        with_recourse: false,
        assignor: ''
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Buscar template de contrato
                const templateResponse = await fetch(`${BACKOFFICE_URL}/services/contract-template/receivables-assignment`);
                if (templateResponse.ok) {
                    const templateData = await templateResponse.json();
                    setCONTRACT_TEMPLATE_ID(templateData.id);

                    // Set API version info if available
                    if (templateData.apiName && templateData.apiVersion) {
                        apiVersion.value = `${templateData.apiName} - ${templateData.apiVersion}`;
                    }
                }
            } catch (error) {
            }
        };

        // Set API version from URL parameters if available
        if (urlApiName && urlApiVersion) {
            apiVersion.value = `${urlApiName} - ${urlApiVersion}`;
        }

        fetchData();
    }, [urlApiName, urlApiVersion]);

    const getOperationTypeName = (type: string | null): string => {
        switch (type) {
            case 'RECEIVABLES_ASSIGNMENT': return 'Cessão de recebíveis';
            case 'STRUCTURED_OPERATION': return 'Operação estruturada';
            case 'ACKNOWLEDGEMENT_OF_DEBT': return 'Confissão de dívidas';
            default: return 'Tipo não definido';
        }
    };

    const getParticipantLabel = (): string => {
        return operationType === 'ACKNOWLEDGEMENT_OF_DEBT' ? 'Devedor' : 'Emitente';
    };

    const handlePersonSearch = (searchValue: string) => {
        setSearchCedente(searchValue);

        // Limpar timer anterior
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }

        // Cancelar requisição anterior
        if (abortController.current) {
            abortController.current.abort();
        }

        if (searchValue.length >= MIN_SEARCH_LENGTH) {
            // Aguardar 500ms após última tecla
            debounceTimer.current = setTimeout(async () => {
                setIsSearchingCedente(true);
                abortController.current = new AbortController();

                try {
                    const token = await Auth.currentSession()
                        .then((user) => user.getIdToken().getJwtToken());

                    const response = await fetch(`${PERSON_SERVICE_URL}/person/ALL/${searchValue}`, {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'x-api-key': API_KEY_PERSON || '',
                            'Content-Type': 'application/json'
                        },
                        signal: abortController.current.signal
                    });

                    if (response.ok) {
                        const data = await response.json();
                        const results = data.results || [];
                        setSearchResults(results.slice(0, MAX_SEARCH_RESULTS));
                        setCedentes(results);
                    }
                } catch (error: any) {
                    if (error.name === 'AbortError') {
                        // Requisição cancelada - ignorar
                        return;
                    }
                } finally {
                    setIsSearchingCedente(false);
                }
            }, SEARCH_DEBOUNCE_MS);
        } else {
            setSearchResults([]);
            setIsSearchingCedente(false);
        }
    };

    const handleSelectCedente = (cedente: any) => {
        const cnpjDoc = cedente.documents?.find((doc: any) => doc.type === 'CNPJ');
        const cpfDoc = cedente.documents?.find((doc: any) => doc.type === 'CPF');
        const document = cnpjDoc?.number || cpfDoc?.number || '';

        if (!document || document.trim() === '') {
            toast.error("Cedente sem documento cadastrado");
            return;
        }

        setSelectedCedente(cedente.uuid);
        setSelectedCedenteName(`${document} - ${cedente.name}`);
        setFormData({ ...formData, assignor: cedente.name });
        setSearchResults([]);
        setSearchCedente('');
    };

    const validateForm = (): boolean => {
        if (!selectedCedente) {
            toast.error(`${getParticipantLabel()} é obrigatório`);
            return false;
        }
        if (!formData.external_link || formData.external_link.trim() === '') {
            toast.error('Card JIRA é obrigatório');
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        if (!API_URL || !API_KEY) {
            toast.error('Configuração da API não encontrada');
            return;
        }

        setIsLoading(true);

        try {
            const token = await Auth.currentSession()
                .then((user) => user.getIdToken().getJwtToken());

            const selectedCedenteData = cedentes.find(c => c.uuid === selectedCedente);

            // Buscar RECIPIENT pelo nome
            const recipientDocument = 'AGROLEND SCFI';
            const recipientResponse = await fetch(`${PERSON_SERVICE_URL}/person/ALL/${recipientDocument}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-api-key': API_KEY_PERSON || ''
                }
            });

            let recipientData = null;
            if (recipientResponse.ok) {
                const data = await recipientResponse.json();
                if (data.results && data.results.length > 0) {
                    recipientData = data.results[0];
                }
            }

            if (!recipientData) {
                toast.error('Erro ao buscar dados do cessionário (RECIPIENT)');
                setIsLoading(false);
                return;
            }

            const getOperationSchemaId = (type: string | null): number | null => {
                switch (type) {
                    case 'RECEIVABLES_ASSIGNMENT': return 2;
                    case 'STRUCTURED_OPERATION': return 3;
                    case 'ACKNOWLEDGEMENT_OF_DEBT': return 4;
                    default: return null;
                }
            };

            const payload = {
                // CreditOperation fields
                operation_type: operationType,
                operation_number: null,
                originator: formData.originator,
                operation_schema_id: getOperationSchemaId(operationType),
                status: 1,
                credit_product_type_name: formData.credit_product_type_name || '',

                // ContractBuilt fields
                contract_built_status: 1,
                register_status: null,
                contract_template_id: CONTRACT_TEMPLATE_ID,

                // ReceivableAssignment fields
                with_recourse: formData.with_recourse,
                participants: [
                    {
                        person_id: selectedCedenteData.uuid,
                        participant_type: "ASSIGNOR",
                        is_primary: true,
                        name: selectedCedenteData.name,
                        document: selectedCedenteData.documents?.find((doc: any) => doc.type === 'CNPJ')?.number || selectedCedenteData.documents?.find((doc: any) => doc.type === 'CPF')?.number,
                    },
                    {
                        person_id: recipientData.uuid,
                        participant_type: "RECIPIENT",
                        is_primary: false,
                        name: recipientData.name,
                        document: recipientData.documents?.find((doc: any) => doc.type === 'CNPJ')?.number || recipientData.documents?.find((doc: any) => doc.type === 'CPF')?.number,
                    }
                ],
                operation_dictionary: {
                    external_link: formData.external_link
                }
            };

            const response = await fetch(`${API_URL}/contracts/receivables_assignment/save`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'x-api-key': API_KEY || ''
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                toast.success('Operação de cessão de recebíveis criada com sucesso!');
                navigate(CONTRACTS_LIST_URL);
            } else {
                const errorData = await response.json();
                toast.error(errorData.message || 'Erro ao salvar operação');
            }
        } catch (error) {
            toast.error('Erro ao conectar com o servidor');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <PageForm
            pageTitle="Nova Operação"
            apiVersion={apiVersion.value}
            isLoading={isLoading}
            breadcrumb={[
                { label: 'Contratos', url: CONTRACTS_LIST_URL },
                { label: 'Nova operação', url: '#' }
            ]}
            controlSlot={(
                <div className="pt-4 flex justify-end">
                    <button
                        onClick={() => navigate(CONTRACTS_LIST_URL)}
                        disabled={isLoading}
                        className="btn-cancel"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={isLoading}
                        className="btn-save"
                    >
                        {isLoading ? 'Salvando...' : 'Salvar'}
                    </button>
                </div>
            )}
        >
            <div className="container mx-auto px-2 py-4">
                <h3 className="text-lg font-semibold mb-4">Dados da Operação</h3>
                <div className="mb-6">
                    <div className="text-lg font-medium">
                        <span className="text-gray-600">Tipo de Operação:</span> {getOperationTypeName(operationType)}
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-10 gap-4 mb-6">
                    <div className="col-span-3">
                        <TextEdit
                            id="originator"
                            label="Originador"
                            value={formData.originator}
                            onChange={(e) => setFormData({ ...formData, originator: e.target.value })}
                        />
                    </div>
                    <div className="col-span-3">
                        <SelectTextField
                            id="credit_product_type_name"
                            label="Tipo de Produto de Crédito"
                            value={formData.credit_product_type_name}
                            onChange={(e) => setFormData({ ...formData, credit_product_type_name: e.target.value })}
                            items={CREDIT_OPERATION_TYPE_OPTIONS}
                        />
                    </div>
                    <div className="col-span-3">
                        <TextEdit
                            id="external_link"
                            label="Card JIRA"
                            value={formData.external_link}
                            onChange={(e) => setFormData({ ...formData, external_link: e.target.value })}
                        />
                    </div>
                    <div className="col-span-4 flex items-center pt-6">
                        <input
                            type="checkbox"
                            id="with_recourse"
                            checked={formData.with_recourse}
                            onChange={(e) => setFormData({ ...formData, with_recourse: e.target.checked })}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                        />
                        <label htmlFor="with_recourse" className="ml-2 text-sm font-medium text-gray-900">
                            Com Regresso
                        </label>
                    </div>
                </div>
                <div className="mb-6">
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {getParticipantLabel()} Selecionado
                        </label>
                        <div className="p-3 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-sm text-gray-900 dark:text-gray-100">
                            {selectedCedenteName || `Nenhum ${getParticipantLabel().toLowerCase()} selecionado`}
                        </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm">
                        <div className="p-4 border-b border-gray-200 dark:border-gray-600">
                            <div className="w-2/5">
                                <TextEdit
                                    id="search_assignor"
                                    label={`Buscar ${getParticipantLabel()}`}
                                    value={searchCedente}
                                    onChange={(e) => handlePersonSearch(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr>
                                        <th className="px-4 py-3 text-left font-medium text-gray-900 dark:text-gray-100" style={{ width: '15%' }}>Documento</th>
                                        <th className="px-4 py-3 text-left font-medium text-gray-900 dark:text-gray-100">Nome</th>
                                        <th className="px-4 py-3 text-left font-medium text-gray-900 dark:text-gray-100">Tipo</th>
                                        <th className="px-4 py-3 text-left font-medium text-gray-900 dark:text-gray-100" style={{ width: '10%' }}>Ação</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isSearchingCedente ? (
                                        <tr>
                                            <td colSpan={4} className="px-4 text-center text-gray-500 dark:text-gray-400" style={{ height: TABLE_LOADING_HEIGHT }}>
                                                <div className="flex items-center justify-center space-x-2">
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                                    <span>Buscando {getParticipantLabel().toLowerCase()}s...</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        Array.from({ length: MAX_SEARCH_RESULTS }, (_, index) => {
                                            const cedente = searchResults[index];
                                            if (cedente) {
                                                const cnpjDoc = cedente.documents?.find((doc: any) => doc.type === 'CNPJ');
                                                const cpfDoc = cedente.documents?.find((doc: any) => doc.type === 'CPF');
                                                const document = cnpjDoc?.number || cpfDoc?.number || '';
                                                const docType = cnpjDoc ? 'CNPJ' : cpfDoc ? 'CPF' : '';
                                                return (
                                                    <tr
                                                        key={cedente.uuid}
                                                        className={getRowClassName(index)}
                                                    >
                                                        <td className="px-4 py-3 text-gray-900 dark:text-gray-100" style={{ width: '15%' }}>{document}</td>
                                                        <td className="px-4 py-3 text-gray-900 dark:text-gray-100">{cedente.name}</td>
                                                        <td className="px-4 py-3 text-gray-900 dark:text-gray-100">{docType}</td>
                                                        <td className="px-4 py-3 text-left" style={{ width: '10%' }}>
                                                            <button
                                                                onClick={() => handleSelectCedente(cedente)}
                                                                className="text-white bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded text-xs px-3 py-1.5 text-center"
                                                            >
                                                                Selecionar
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            } else {
                                                return (
                                                    <tr key={index} className={getRowClassName(index)}>
                                                        <td className="px-4 py-3 text-gray-400 dark:text-gray-500" style={{ width: '15%' }}>-</td>
                                                        <td className="px-4 py-3 text-gray-400 dark:text-gray-500">-</td>
                                                        <td className="px-4 py-3 text-gray-400 dark:text-gray-500">-</td>
                                                        <td className="px-4 py-3 text-gray-400 dark:text-gray-500 text-left" style={{ width: '10%' }}>-</td>
                                                    </tr>
                                                );
                                            }
                                        }))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </PageForm>
    );
};

export default OperationReceivablesAssignmentFormPage;